import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessRegistrationContext = createContext();

export const useBusinessRegistration = () => {
    const context = useContext(BusinessRegistrationContext);
    if (!context) {
        throw new Error('useBusinessRegistration must be used within a BusinessRegistrationProvider');
    }
    return context;
};

export const BusinessRegistrationProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { makeAuthenticatedRequest, loginType } = useAuth();

    // API base URL for business registration endpoints
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

    // Determine the correct base URL based on login type (for admin/master endpoints)
    const getServiceBaseUrl = useCallback(() => {
        if (loginType === 'master') {
            return `${API_BASE_URL}/master/business-registration/api/v1/business-registration`;
        } else if (loginType === 'admin') {
            return `${API_BASE_URL}/admin/business-registration/api/v1/business-registration`;
        } else {
            throw new Error('Invalid login type for business registration management');
        }
    }, [API_BASE_URL, loginType]);

    // Public registration URL (no authentication required)
    const getPublicRegistrationUrl = useCallback(() => {
        return `${API_BASE_URL}/public/business-registration/api/v1/business-registration`;
    }, [API_BASE_URL]);

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const data = await response.json();

        if (!response.ok) {
            // Handle specific error cases from the backend
            if (response.status === 409) {
                // Conflict - likely duplicate email, phone, or registry number
                throw new Error(data.error || 'Data already exists');
            } else if (response.status === 400) {
                // Bad request - validation errors
                throw new Error(data.error || 'Invalid request data');
            } else if (response.status === 404) {
                // Not found
                throw new Error(data.error || 'Resource not found');
            } else if (response.status === 500) {
                // Server error
                throw new Error(data.error || 'Internal server error');
            }

            throw new Error(data.error || data.message || 'Request failed');
        }

        return data;
    };

    // Clear error state
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Validate admin/master access
    const validateAccess = useCallback(() => {
        if (loginType !== 'admin' && loginType !== 'master') {
            throw new Error('Only admin and master users can access business registration management');
        }
    }, [loginType]);

    // PUBLIC ENDPOINT - Register a new business (no authentication required)
    // Update the registerBusiness function to handle verification flags
    const registerBusiness = useCallback(async (registrationData) => {
        if (!registrationData) {
            throw new Error('Registration data is required');
        }

        // Validate required fields - Updated to match backend validation
        const requiredFields = ['business_name', 'business_owner', 'business_email', 'business_phone', 'password', 'business_terms'];
        const missingFields = requiredFields.filter(field => !registrationData[field]);

        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        if (!registrationData.business_terms) {
            throw new Error('Business terms must be accepted');
        }

        // Additional validation to match backend requirements
        if (registrationData.password && registrationData.password.length < 8) {
            throw new Error('Password must be at least 8 characters long');
        }

        if (registrationData.business_name && registrationData.business_name.length > 255) {
            throw new Error('Business name must not exceed 255 characters');
        }

        if (registrationData.business_owner && registrationData.business_owner.length > 255) {
            throw new Error('Business owner must not exceed 255 characters');
        }

        if (registrationData.business_email && registrationData.business_email.length > 255) {
            throw new Error('Business email must not exceed 255 characters');
        }

        if (registrationData.business_phone && registrationData.business_phone.length > 255) {
            throw new Error('Business phone must not exceed 255 characters');
        }

        if (registrationData.business_category && registrationData.business_category.length > 255) {
            throw new Error('Business category must not exceed 255 characters');
        }

        if (registrationData.business_description && registrationData.business_description.length > 2000) {
            throw new Error('Business description must not exceed 2000 characters');
        }

        // NEW: Validate verification flags if provided
        if (registrationData.phone_verified !== undefined && typeof registrationData.phone_verified !== 'boolean') {
            throw new Error('Phone verification status must be a boolean value');
        }

        if (registrationData.email_verified !== undefined && typeof registrationData.email_verified !== 'boolean') {
            throw new Error('Email verification status must be a boolean value');
        }

        setLoading(true);
        setError(null);

        try {
            const publicUrl = getPublicRegistrationUrl();

            // Prepare registration payload with optional verification flags
            const registrationPayload = {
                ...registrationData,
                // Include verification flags if provided (they'll be ignored if undefined)
                ...(registrationData.phone_verified !== undefined && { phone_verified: registrationData.phone_verified }),
                ...(registrationData.email_verified !== undefined && { email_verified: registrationData.email_verified })
            };

            // Make public request (no authentication headers)
            const response = await fetch(`${publicUrl}/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(registrationPayload),
            });

            const result = await handleApiResponse(response);

            // The enhanced backend now returns additional information
            return {
                ...result,
                // Backend now includes message about welcome email
                registrationComplete: true,
                defaultDataCreated: true, // Indicates that subscription, employee, schedule, and verification were created
                verificationInfo: {
                    phoneVerified: registrationData.phone_verified || false,
                    emailVerified: registrationData.email_verified || false,
                    adminVerified: (registrationData.phone_verified && registrationData.email_verified) || false
                }
            };
        } catch (err) {
            const errorMessage = err.message || 'Failed to register business';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [getPublicRegistrationUrl]);

    // NEW: Check if email/phone/registry number exists before registration
    const checkExistence = useCallback(async (field, value) => {
        if (!field || !value) {
            throw new Error('Field and value are required');
        }

        try {
            const publicUrl = getPublicRegistrationUrl();

            const response = await fetch(`${publicUrl}/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ field, value }),
            });

            const result = await handleApiResponse(response);
            return result.exists;
        } catch (err) {
            const errorMessage = err.message || 'Failed to check existence';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    }, [getPublicRegistrationUrl]);

    // Helper functions for specific existence checks
    const checkEmailExists = useCallback(async (email) => {
        return await checkExistence('email', email);
    }, [checkExistence]);

    const checkPhoneExists = useCallback(async (phone) => {
        return await checkExistence('phone', phone);
    }, [checkExistence]);

    const checkRegistryNumberExists = useCallback(async (registryNumber) => {
        return await checkExistence('registry_number', registryNumber);
    }, [checkExistence]);

    // ADMIN/MASTER ENDPOINTS - Manage business registrations

    // Get all business registrations with pagination and filters
    const getBusinessRegistrations = useCallback(async (options = {}) => {
        validateAccess();

        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();

            if (options.limit) queryParams.append('limit', options.limit);
            if (options.offset) queryParams.append('offset', options.offset);
            if (options.businessCategory) queryParams.append('business_category', options.businessCategory);
            if (options.businessYear) queryParams.append('business_year', options.businessYear);
            if (options.verifiedEmail !== undefined) queryParams.append('verified_email', options.verifiedEmail);
            if (options.verifiedPhone !== undefined) queryParams.append('verified_phone', options.verifiedPhone);
            if (options.registrationFrom) queryParams.append('registration_from', options.registrationFrom);
            if (options.registrationTo) queryParams.append('registration_to', options.registrationTo);
            if (options.search) queryParams.append('search', options.search);

            const baseUrl = getServiceBaseUrl();
            const url = `${baseUrl}/registrations`;
            const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;

            const response = await makeAuthenticatedRequest(fullUrl);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to fetch business registrations';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // Get business registration by registry number
    const getBusinessRegistrationByRegistryNumber = useCallback(async (registryNumber) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to get business registration';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // Get business locations by registry number
    const getBusinessLocations = useCallback(async (registryNumber) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/locations`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to get business locations';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // Add business location
    const addBusinessLocation = useCallback(async (registryNumber, locationData) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        if (!locationData) {
            throw new Error('Location data is required');
        }

        // Validate required location fields - Updated to match backend validation
        const requiredFields = ['location_latitude', 'location_longitude', 'location_address_1', 'location_city', 'location_country', 'location_zip'];
        const missingFields = requiredFields.filter(field => locationData[field] === undefined || locationData[field] === null);

        if (missingFields.length > 0) {
            throw new Error(`Missing required location fields: ${missingFields.join(', ')}`);
        }

        // Additional validation to match backend
        if (locationData.location_latitude < -90 || locationData.location_latitude > 90) {
            throw new Error('Latitude must be between -90 and 90');
        }

        if (locationData.location_longitude < -180 || locationData.location_longitude > 180) {
            throw new Error('Longitude must be between -180 and 180');
        }

        if (locationData.location_address_1.length > 255) {
            throw new Error('Address must not exceed 255 characters');
        }

        if (locationData.location_city.length > 50) {
            throw new Error('City must not exceed 50 characters');
        }

        if (locationData.location_country.length > 50) {
            throw new Error('Country must not exceed 50 characters');
        }

        if (locationData.location_zip.length > 10) {
            throw new Error('ZIP code must not exceed 10 characters');
        }

        if (locationData.location_scenery_type && locationData.location_scenery_type.length > 80) {
            throw new Error('Scenery type must not exceed 80 characters');
        }

        if (locationData.location_count !== undefined && locationData.location_count < 0) {
            throw new Error('Location count must be non-negative');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/locations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(locationData),
            });

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to add business location';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // NEW: Get business subscription information
    const getBusinessSubscription = useCallback(async (registryNumber) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/subscription`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to get business subscription';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // NEW: Re-default business subscription evaluation
    const reDefaultSubscriptionEvaluation = useCallback(async (registryNumber) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/subscription-evaluation/re-default`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to re-default subscription evaluation';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // NEW: Get business employees
    const getBusinessEmployees = useCallback(async (registryNumber) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/employees`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to get business employees';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // NEW: Get business schedule
    const getBusinessSchedule = useCallback(async (registryNumber) => {
        validateAccess();

        if (!registryNumber) {
            throw new Error('Registry number is required');
        }

        setLoading(true);
        setError(null);

        try {
            const baseUrl = getServiceBaseUrl();
            const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/schedule`);
            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Failed to get business schedule';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, validateAccess, getServiceBaseUrl]);

    // SEARCH AND FILTER FUNCTIONS

    // Search business registrations
    const searchBusinessRegistrations = useCallback(async (searchTerm, options = {}) => {
        validateAccess();

        const searchOptions = {
            ...options,
            search: searchTerm,
        };

        return await getBusinessRegistrations(searchOptions);
    }, [getBusinessRegistrations, validateAccess]);

    // Get registrations by verification status
    const getRegistrationsByVerificationStatus = useCallback(async (emailVerified, phoneVerified, options = {}) => {
        validateAccess();

        const filterOptions = {
            ...options,
            verifiedEmail: emailVerified,
            verifiedPhone: phoneVerified,
        };

        return await getBusinessRegistrations(filterOptions);
    }, [getBusinessRegistrations, validateAccess]);

    // Get registrations by category
    const getRegistrationsByCategory = useCallback(async (category, options = {}) => {
        validateAccess();

        const filterOptions = {
            ...options,
            businessCategory: category,
        };

        return await getBusinessRegistrations(filterOptions);
    }, [getBusinessRegistrations, validateAccess]);

    // Get registrations by year
    const getRegistrationsByYear = useCallback(async (year, options = {}) => {
        validateAccess();

        const filterOptions = {
            ...options,
            businessYear: year,
        };

        return await getBusinessRegistrations(filterOptions);
    }, [getBusinessRegistrations, validateAccess]);

    // Get registrations by date range
    const getRegistrationsByDateRange = useCallback(async (fromDate, toDate, options = {}) => {
        validateAccess();

        const filterOptions = {
            ...options,
            registrationFrom: fromDate,
            registrationTo: toDate,
        };

        return await getBusinessRegistrations(filterOptions);
    }, [getBusinessRegistrations, validateAccess]);

    // Get unverified registrations
    const getUnverifiedRegistrations = useCallback(async (options = {}) => {
        validateAccess();
        return await getRegistrationsByVerificationStatus(false, false, options);
    }, [getRegistrationsByVerificationStatus, validateAccess]);

    // Get partially verified registrations (email OR phone verified, but not both)
    const getPartiallyVerifiedRegistrations = useCallback(async (options = {}) => {
        validateAccess();

        try {
            const [emailOnly, phoneOnly] = await Promise.all([
                getRegistrationsByVerificationStatus(true, false, options),
                getRegistrationsByVerificationStatus(false, true, options)
            ]);

            return {
                registrations: [...emailOnly.registrations, ...phoneOnly.registrations],
                total_count: emailOnly.total_count + phoneOnly.total_count,
                email_only: emailOnly.registrations,
                phone_only: phoneOnly.registrations,
                ...options
            };
        } catch (err) {
            const errorMessage = err.message || 'Failed to get partially verified registrations';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    }, [getRegistrationsByVerificationStatus, validateAccess]);

    // Get fully verified registrations
    const getFullyVerifiedRegistrations = useCallback(async (options = {}) => {
        validateAccess();
        return await getRegistrationsByVerificationStatus(true, true, options);
    }, [getRegistrationsByVerificationStatus, validateAccess]);

    // UTILITY FUNCTIONS

    // Enhanced validation to match backend exactly
    // Enhanced validation to match backend exactly with verification flags
    const validateRegistrationData = useCallback((data) => {
        const errors = [];

        if (!data.business_name || data.business_name.trim().length === 0) {
            errors.push({ field: 'business_name', message: 'Business name is required' });
        } else if (data.business_name.length > 255) {
            errors.push({ field: 'business_name', message: 'Business name must not exceed 255 characters' });
        }

        if (!data.business_owner || data.business_owner.trim().length === 0) {
            errors.push({ field: 'business_owner', message: 'Business owner is required' });
        } else if (data.business_owner.length > 255) {
            errors.push({ field: 'business_owner', message: 'Business owner must not exceed 255 characters' });
        }

        if (!data.business_email || data.business_email.trim().length === 0) {
            errors.push({ field: 'business_email', message: 'Business email is required' });
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.business_email)) {
            errors.push({ field: 'business_email', message: 'Please enter a valid email address' });
        } else if (data.business_email.length > 255) {
            errors.push({ field: 'business_email', message: 'Business email must not exceed 255 characters' });
        }

        if (!data.business_phone || data.business_phone.trim().length === 0) {
            errors.push({ field: 'business_phone', message: 'Business phone is required' });
        } else if (data.business_phone.length > 255) {
            errors.push({ field: 'business_phone', message: 'Business phone must not exceed 255 characters' });
        }

        if (!data.password || data.password.length === 0) {
            errors.push({ field: 'password', message: 'Password is required' });
        } else if (data.password.length < 8) {
            errors.push({ field: 'password', message: 'Password must be at least 8 characters long' });
        }

        if (!data.business_terms) {
            errors.push({ field: 'business_terms', message: 'You must accept the business terms' });
        }

        if (data.business_category && data.business_category.length > 255) {
            errors.push({ field: 'business_category', message: 'Business category must not exceed 255 characters' });
        }

        if (data.business_description && data.business_description.length > 2000) {
            errors.push({ field: 'business_description', message: 'Business description must not exceed 2000 characters' });
        }

        if (data.business_year && (data.business_year < 1900 || data.business_year > new Date().getFullYear())) {
            errors.push({ field: 'business_year', message: 'Please enter a valid business year' });
        }

        // NEW: Validate verification flags
        if (data.phone_verified !== undefined && typeof data.phone_verified !== 'boolean') {
            errors.push({ field: 'phone_verified', message: 'Phone verification status must be true or false' });
        }

        if (data.email_verified !== undefined && typeof data.email_verified !== 'boolean') {
            errors.push({ field: 'email_verified', message: 'Email verification status must be true or false' });
        }

        return errors;
    }, []);

    // Enhanced location validation to match backend exactly
    const validateLocationData = useCallback((data) => {
        const errors = [];

        if (data.location_latitude === undefined || data.location_latitude === null) {
            errors.push({ field: 'location_latitude', message: 'Latitude is required' });
        } else if (data.location_latitude < -90 || data.location_latitude > 90) {
            errors.push({ field: 'location_latitude', message: 'Latitude must be between -90 and 90' });
        }

        if (data.location_longitude === undefined || data.location_longitude === null) {
            errors.push({ field: 'location_longitude', message: 'Longitude is required' });
        } else if (data.location_longitude < -180 || data.location_longitude > 180) {
            errors.push({ field: 'location_longitude', message: 'Longitude must be between -180 and 180' });
        }

        if (!data.location_address_1 || data.location_address_1.trim().length === 0) {
            errors.push({ field: 'location_address_1', message: 'Address is required' });
        } else if (data.location_address_1.length > 255) {
            errors.push({ field: 'location_address_1', message: 'Address must not exceed 255 characters' });
        }

        if (!data.location_city || data.location_city.trim().length === 0) {
            errors.push({ field: 'location_city', message: 'City is required' });
        } else if (data.location_city.length > 50) {
            errors.push({ field: 'location_city', message: 'City must not exceed 50 characters' });
        }

        if (!data.location_country || data.location_country.trim().length === 0) {
            errors.push({ field: 'location_country', message: 'Country is required' });
        } else if (data.location_country.length > 50) {
            errors.push({ field: 'location_country', message: 'Country must not exceed 50 characters' });
        }

        if (!data.location_zip || data.location_zip.trim().length === 0) {
            errors.push({ field: 'location_zip', message: 'ZIP code is required' });
        } else if (data.location_zip.length > 10) {
            errors.push({ field: 'location_zip', message: 'ZIP code must not exceed 10 characters' });
        }

        if (data.location_address_2 && data.location_address_2.length > 255) {
            errors.push({ field: 'location_address_2', message: 'Address line 2 must not exceed 255 characters' });
        }

        if (data.location_scenery_type && data.location_scenery_type.length > 80) {
            errors.push({ field: 'location_scenery_type', message: 'Scenery type must not exceed 80 characters' });
        }

        if (data.location_count !== undefined && data.location_count !== null && data.location_count < 0) {
            errors.push({ field: 'location_count', message: 'Location count must be non-negative' });
        }

        return errors;
    }, []);

    // Format registration data for display
    const formatRegistrationData = useCallback((registration) => {
        const formatDate = (dateString) => {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        const getVerificationStatus = () => {
            const emailVerified = registration.verified_email;
            const phoneVerified = registration.verified_phone;

            if (emailVerified && phoneVerified) return { status: 'fully_verified', text: 'Fully Verified', color: 'green' };
            if (emailVerified || phoneVerified) return { status: 'partially_verified', text: 'Partially Verified', color: 'orange' };
            return { status: 'unverified', text: 'Unverified', color: 'red' };
        };

        const verification = getVerificationStatus();

        return {
            ...registration,
            formattedRegistrationDate: formatDate(registration.registration_date),
            formattedCreatedAt: formatDate(registration.business_created_at),
            verificationStatus: verification,
            emailVerificationText: registration.verified_email ? 'Verified' : 'Unverified',
            phoneVerificationText: registration.verified_phone ? 'Verified' : 'Unverified',
            businessAge: registration.business_year
                ? `${new Date().getFullYear() - registration.business_year} years`
                : 'N/A',
            shortDescription: registration.business_description && registration.business_description.length > 100
                ? registration.business_description.substring(0, 100) + '...'
                : registration.business_description || 'No description',
            registryNumberShort: registration.business_registry_number
                ? registration.business_registry_number.split('-').pop()
                : 'N/A',
        };
    }, []);

    // Export registrations data
    const exportRegistrationsData = useCallback(async (filters = {}) => {
        validateAccess();

        // Get all registrations without pagination for export
        const exportOptions = {
            ...filters,
            limit: 10000, // Large limit for export
            offset: 0,
        };

        const result = await getBusinessRegistrations(exportOptions);

        // Transform data for export
        const exportData = result.registrations.map(registration => ({
            'Registry Number': registration.business_registry_number,
            'Business Name': registration.business_name,
            'Owner': registration.business_owner,
            'Email': registration.business_email,
            'Phone': registration.business_phone,
            'Category': registration.business_category || 'N/A',
            'Year Founded': registration.business_year || 'N/A',
            'Email Verified': registration.verified_email ? 'Yes' : 'No',
            'Phone Verified': registration.verified_phone ? 'Yes' : 'No',
            'Registration Date': new Date(registration.registration_date).toLocaleDateString(),
            'Description': registration.business_description || 'N/A',
            'IP Address': registration.ip_address,
            'Device Type': registration.device_type || 'N/A',
            'Referral Source': registration.referral_source || 'N/A',
        }));

        return {
            data: exportData,
            totalCount: result.total_count,
            filename: `business_registrations_${new Date().toISOString().split('T')[0]}.csv`
        };
    }, [getBusinessRegistrations, validateAccess]);

    // NEW: Helper function to determine verification status from flags
    const getVerificationStatusFromFlags = useCallback((phoneVerified, emailVerified) => {
        const adminVerified = phoneVerified && emailVerified;

        return {
            phoneVerified: phoneVerified || false,
            emailVerified: emailVerified || false,
            userVerified: adminVerified,
            systemVerified: adminVerified,
            adminVerified: adminVerified,
            isFullyVerified: adminVerified,
            isPartiallyVerified: (phoneVerified || emailVerified) && !adminVerified,
            isUnverified: !phoneVerified && !emailVerified
        };
    }, []);

    // NEW: Validate verification combination
    const validateVerificationCombination = useCallback((phoneVerified, emailVerified) => {
        const warnings = [];

        if (phoneVerified && !emailVerified) {
            warnings.push('Phone is verified but email is not. Admin verification will be false.');
        }

        if (emailVerified && !phoneVerified) {
            warnings.push('Email is verified but phone is not. Admin verification will be false.');
        }

        if (phoneVerified && emailVerified) {
            warnings.push('Both phone and email are verified. Admin verification will be true.');
        }

        if (!phoneVerified && !emailVerified) {
            warnings.push('Neither phone nor email are verified. All verification flags will be false.');
        }

        return warnings;
    }, []);

    // NEW: Format verification data for display
    const formatVerificationData = useCallback((verificationData) => {
        if (!verificationData) return null;

        const status = getVerificationStatusFromFlags(
            verificationData.phone_verified,
            verificationData.email_verified
        );

        return {
            ...verificationData,
            verificationStatus: status,
            verificationSummary: status.isFullyVerified
                ? 'Fully Verified'
                : status.isPartiallyVerified
                    ? 'Partially Verified'
                    : 'Unverified',
            verificationColor: status.isFullyVerified
                ? 'green'
                : status.isPartiallyVerified
                    ? 'orange'
                    : 'red'
        };
    }, [getVerificationStatusFromFlags]);

    // Get registration statistics
    const getRegistrationStatistics = useCallback(async (dateRange = {}) => {
        validateAccess();

        try {
            const result = await getBusinessRegistrations({
                limit: 10000,
                ...dateRange
            });
            const registrations = result.registrations;

            if (registrations.length === 0) {
                return {
                    totalRegistrations: 0,
                    verifiedEmails: 0,
                    verifiedPhones: 0,
                    fullyVerified: 0,
                    categoryCounts: {},
                    yearCounts: {},
                    monthlyRegistrations: {},
                    averageRegistrationsPerDay: 0,
                };
            }

            const verifiedEmails = registrations.filter(r => r.verified_email).length;
            const verifiedPhones = registrations.filter(r => r.verified_phone).length;
            const fullyVerified = registrations.filter(r => r.verified_email && r.verified_phone).length;

            // Category distribution
            const categoryCounts = {};
            registrations.forEach(r => {
                const category = r.business_category || 'Uncategorized';
                categoryCounts[category] = (categoryCounts[category] || 0) + 1;
            });

            // Year distribution
            const yearCounts = {};
            registrations.forEach(r => {
                if (r.business_year) {
                    yearCounts[r.business_year] = (yearCounts[r.business_year] || 0) + 1;
                }
            });

            // Monthly registration counts
            const monthlyRegistrations = {};
            registrations.forEach(r => {
                const date = new Date(r.registration_date);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                monthlyRegistrations[monthKey] = (monthlyRegistrations[monthKey] || 0) + 1;
            });

            // Calculate average registrations per day
            const oldestDate = new Date(Math.min(...registrations.map(r => new Date(r.registration_date))));
            const newestDate = new Date(Math.max(...registrations.map(r => new Date(r.registration_date))));
            const daysDiff = Math.max(1, Math.ceil((newestDate - oldestDate) / (1000 * 60 * 60 * 24)));
            const averageRegistrationsPerDay = registrations.length / daysDiff;

            return {
                totalRegistrations: registrations.length,
                verifiedEmails,
                verifiedPhones,
                fullyVerified,
                emailVerificationRate: ((verifiedEmails / registrations.length) * 100).toFixed(1) + '%',
                phoneVerificationRate: ((verifiedPhones / registrations.length) * 100).toFixed(1) + '%',
                fullVerificationRate: ((fullyVerified / registrations.length) * 100).toFixed(1) + '%',
                categoryCounts,
                yearCounts,
                monthlyRegistrations,
                averageRegistrationsPerDay: averageRegistrationsPerDay.toFixed(1),
                dateRange: {
                    from: oldestDate.toISOString().split('T')[0],
                    to: newestDate.toISOString().split('T')[0]
                }
            };
        } catch (err) {
            const errorMessage = err.message || 'Failed to get registration statistics';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    }, [getBusinessRegistrations, validateAccess]);

    // Health check
    const checkHealth = useCallback(async () => {
        try {
            const healthUrl = loginType === 'master'
                ? `${API_BASE_URL}/master/business-registration/health`
                : loginType === 'admin'
                    ? `${API_BASE_URL}/admin/business-registration/health`
                    : `${API_BASE_URL}/public/business-registration/health`;

            const response = loginType === 'admin' || loginType === 'master'
                ? await makeAuthenticatedRequest(healthUrl)
                : await fetch(healthUrl);

            const result = await handleApiResponse(response);
            return result;
        } catch (err) {
            const errorMessage = err.message || 'Health check failed';
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    }, [makeAuthenticatedRequest, API_BASE_URL, loginType]);

    // Get current endpoint info for debugging
    const getEndpointInfo = useCallback(() => {
        return {
            loginType: loginType || 'public',
            publicUrl: getPublicRegistrationUrl(),
            managementUrl: loginType === 'master'
                ? `${API_BASE_URL}/master/business-registration/api/v1/business-registration`
                : loginType === 'admin'
                    ? `${API_BASE_URL}/admin/business-registration/api/v1/business-registration`
                    : 'N/A (requires login)',
            accessLevel: loginType === 'master' ? 'Master Access' :
                loginType === 'admin' ? 'Admin Access' : 'Public Access',
            requiredHeaders: loginType === 'master'
                ? ['Authorization', 'secret_credential']
                : loginType === 'admin'
                    ? ['Authorization', 'admin_id']
                    : ['none (public registration)']
        };
    }, [loginType, API_BASE_URL, getPublicRegistrationUrl]);

    const value = {
        // State
        loading,
        error,

        // Public Actions (no authentication required)
        registerBusiness,
        checkEmailExists,
        checkPhoneExists,
        checkRegistryNumberExists,
        checkExistence,

        // Management Actions (admin/master access required)
        getBusinessRegistrations,
        getBusinessRegistrationByRegistryNumber,
        getBusinessLocations,
        addBusinessLocation,
        getBusinessSubscription,
        reDefaultSubscriptionEvaluation, // NEW FUNCTION ADDED
        getBusinessEmployees,
        getBusinessSchedule,

        // Search and Filter Actions
        searchBusinessRegistrations,
        getRegistrationsByVerificationStatus,
        getRegistrationsByCategory,
        getRegistrationsByYear,
        getRegistrationsByDateRange,
        getUnverifiedRegistrations,
        getPartiallyVerifiedRegistrations,
        getFullyVerifiedRegistrations,

        // Utility Actions
        exportRegistrationsData,
        getRegistrationStatistics,
        checkHealth,

        // Validation Helpers
        validateRegistrationData,
        validateLocationData,
        formatRegistrationData,
        clearError,
        getEndpointInfo,

        // NEW: Verification Helpers
        getVerificationStatusFromFlags,
        validateVerificationCombination,
        formatVerificationData,

        // Computed values
        isPublicUser: !loginType,
        isAdminUser: loginType === 'admin',
        isMasterUser: loginType === 'master',
        hasManagementAccess: loginType === 'admin' || loginType === 'master',
        currentAccessLevel: loginType || 'public',
    };
    return (
        <BusinessRegistrationContext.Provider value={value}>
            {children}
        </BusinessRegistrationContext.Provider>
    );
};